import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createRevenue as trCreateRevenue,
  updateRevenue as trUpdateRevenue,
  deleteRevenue as trDeleteRevenue,
  deleteVariableExpense as trDeleteVariableExpense,
} from '../../../api/treasury';
import AddRevenueMenu from '../billing/AddRevenueMenu';
import RevenueList from '../billing/RevenueList';
import RevenueDetailPanel from '../billing/RevenueDetailPanel';
import { PRESETS_BY_ID, applyPreset } from '../billing/presets';
import { STANDARD_REVENUE_PACK } from '../billing/standardPack';
import { longMonthLabels } from '../billing/monthLabels';
import { rotateValuesFromDisplay } from '../billing/monthRotation';
import { totalAnnualHt, monthlyCashCollection } from '../billing/computeBillingKpis';
import { useP } from '../../../i18n/useP';
import '../billing/BillingTab.css';

function fmtK(v) {
  return Math.round(v / 1000).toLocaleString('fr-CH').replace(/ /g, '’').replace(/\s/g, '’') + ' k';
}

// Treasury → Facturation tab. Split layout : the chart and KPI banner sit on
// top, then a 2-column "list-then-focus" composition (left rail = revenue
// list, right pane = single-line editor) replaces the previous flat table.
//
// Backend stores `vat_rate` as a decimal (0.081). compute helpers expect a
// percentage (8.1), so we convert at the call site.
export default function RevenuesTab({
  tenantId,
  scenarioId,
  scenario,
  locked,
  revenues,
  variables = [],
  onChange,
  onChangeVariables,
  showChart = true,
  hideTitle = false,
  // Onglet partagé Finance (Trésorerie) / Ventes (12MR). Le vendeur n'a pas le
  // périmètre `financials` : ses écritures passent par le préfixe ouvert à
  // `sales`. Même logique serveur des deux côtés, seule la porte change.
  api,
}) {
  const createRevenue = api?.createRevenue || trCreateRevenue;
  const updateRevenue = api?.updateRevenue || trUpdateRevenue;
  const deleteRevenue = api?.deleteRevenue || trDeleteRevenue;
  const deleteVariableExpense = api?.deleteVariableExpense || trDeleteVariableExpense;
  const { t } = useTranslation();
  const p = useP();
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  // When we just POSTed a new row, we know its id but the parent's
  // revenues list hasn't refreshed yet. Park the id here ; the focus
  // useEffect picks it up as soon as the row appears in sortedRevenues.
  const [pendingFocusId, setPendingFocusId] = useState(null);

  const vatRatePct = (Number(scenario?.vat_rate) || 0.081) * 100;
  const startMonth = Number.isInteger(scenario?.start_month) ? scenario.start_month : 0;
  const monthLabels = longMonthLabels(startMonth);

  // Liste unique : toutes les lignes de revenus (la nature prestation/marchandise
  // vit désormais par ligne via goods_pct, plus de filtre par onglet).
  const sortedRevenues = [...(revenues || [])]
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  // Ligne synthétique « Total » : somme mensuelle de toutes les lignes, lecture
  // seule. Affichée en tête de liste (si showChart) ; son détail = graphe agrégé.
  const TOTAL_ID = '__total__';
  const totalRevenue = showChart && sortedRevenues.length > 0 ? {
    id: TOTAL_ID,
    label: p('Total'),
    detail: null,
    monthly_values: Array.from({ length: 12 }, (_, m) =>
      sortedRevenues.reduce((s, r) => s + (Number((r.monthly_values || [])[m]) || 0), 0)),
    payment_lag: 0,
    vat_applicable: false,
    goods_pct: 0,
  } : null;

  // Focus logic. Priorities :
  //  1. If a pending id (freshly created) shows up in the list, focus it.
  //  2. If nothing pending and the current selection has been removed,
  //     fall back to the first row.
  //  3. If the list is empty, clear the selection.
  useEffect(() => {
    if (pendingFocusId && sortedRevenues.find((r) => r.id === pendingFocusId)) {
      setSelectedId(pendingFocusId);
      setPendingFocusId(null);
      return;
    }
    if (pendingFocusId) return; // wait for the new row to land
    if (selectedId === TOTAL_ID) return; // la ligne Total est une sélection valide
    if (sortedRevenues.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!sortedRevenues.find((r) => r.id === selectedId)) {
      setSelectedId(sortedRevenues[0].id);
    }
  }, [sortedRevenues, selectedId, pendingFocusId]);

  const isTotalSelected = selectedId === TOTAL_ID && !!totalRevenue;
  const selectedIndex = sortedRevenues.findIndex((r) => r.id === selectedId);
  const selectedRevenue = isTotalSelected
    ? totalRevenue
    : (selectedIndex >= 0 ? sortedRevenues[selectedIndex] : null);

  async function patch(rid, payload) {
    const r = await updateRevenue(tenantId, scenarioId, rid, payload);
    if (r.error) setError(r.error);
    else { setError(null); onChange(); }
  }

  // Reorder : déplace la ligne `idx` d'un cran (direction = -1 monte, +1 descend)
  // puis renormalise `display_order` sur les indices visuels (0..n-1). Renormaliser
  // est nécessaire car les anciennes lignes héritent toutes du server_default = 0 :
  // un simple swap de valeurs serait un no-op entre voisines à display_order égal.
  // On ne PATCH que les lignes dont la valeur change réellement.
  async function swapOrder(idx, direction) {
    const target = idx + direction;
    if (target < 0 || target >= sortedRevenues.length) return;
    const next = [...sortedRevenues];
    [next[idx], next[target]] = [next[target], next[idx]];
    const patches = next
      .map((row, newIdx) => ({ row, newIdx }))
      .filter(({ row, newIdx }) => row.display_order !== newIdx);
    if (patches.length === 0) return;
    const results = await Promise.all(
      patches.map(({ row, newIdx }) =>
        updateRevenue(tenantId, scenarioId, row.id, { display_order: newIdx })
      )
    );
    const firstError = results.find((r) => r.error);
    if (firstError) setError(firstError.error);
    else { setError(null); onChange(); }
  }

  async function addBlankRow() {
    const r = await createRevenue(tenantId, scenarioId, {
      scenario_id: scenarioId,
      label: p('Nouvelle ligne'),
      monthly_values: Array(12).fill(0),
      payment_lag: 0,
      vat_applicable: true,
      display_order: sortedRevenues.length,
      goods_pct: 0,
    });
    if (r.error) setError(r.error);
    else { setError(null); if (r.data?.id) setPendingFocusId(r.data.id); onChange(); }
  }

  async function addPresetRow(presetId) {
    const preset = PRESETS_BY_ID[presetId];
    if (!preset) return;
    const scenarioOrder = applyPreset(presetId, preset.defaultParams);
    const r = await createRevenue(tenantId, scenarioId, {
      scenario_id: scenarioId,
      label: preset.labelFr,
      monthly_values: rotateValuesFromDisplay(scenarioOrder, startMonth),
      payment_lag: 1,
      vat_applicable: true,
      display_order: sortedRevenues.length,
      goods_pct: 0,
    });
    if (r.error) setError(r.error);
    else { setError(null); if (r.data?.id) setPendingFocusId(r.data.id); onChange(); }
  }

  async function duplicateRow(rev) {
    if (!rev) return;
    const r = await createRevenue(tenantId, scenarioId, {
      scenario_id: scenarioId,
      label: `${rev.label || p('Ligne')} ${p('(copie)')}`,
      detail: rev.detail || null,
      monthly_values: [...(rev.monthly_values || Array(12).fill(0))],
      payment_lag: rev.payment_lag ?? 0,
      vat_applicable: !!rev.vat_applicable,
      display_order: sortedRevenues.length,
      goods_pct: Number(rev.goods_pct) || 0,
    });
    if (r.error) setError(r.error);
    else { setError(null); if (r.data?.id) setPendingFocusId(r.data.id); onChange(); }
  }

  async function loadStandardPack() {
    const baseOrder = sortedRevenues.length;
    const results = await Promise.all(
      STANDARD_REVENUE_PACK.map((line, idx) =>
        createRevenue(tenantId, scenarioId, {
          scenario_id: scenarioId,
          label: line.label,
          detail: line.detail || null,
          // Standard pack values are authored scenario-relative ; rotate to
          // calendar before persistence.
          monthly_values: rotateValuesFromDisplay(line.monthly_values, startMonth),
          payment_lag: line.payment_lag,
          vat_applicable: line.vat_applicable,
          display_order: baseOrder + idx,
        })
      )
    );
    const firstError = results.find((r) => r.error);
    if (firstError) setError(firstError.error);
    else { setError(null); onChange(); }
  }

  async function removeRow(rid) {
    if (!rid) return;
    if (!window.confirm(p('Supprimer cette ligne ?'))) return;
    // Le coût marchandise lié (charge pct_revenue) part avec la ligne :
    // on le supprime d'abord, sinon le backend bloque (FK NO ACTION → 409).
    const linked = (variables || []).filter(
      (v) => v.driver_type === 'pct_revenue' && v.revenue_ref_id === rid
    );
    for (const v of linked) {
      const rv = await deleteVariableExpense(tenantId, scenarioId, v.id);
      if (rv.error) { setError(rv.error); return; }
    }
    if (linked.length) onChangeVariables && onChangeVariables();
    const r = await deleteRevenue(tenantId, scenarioId, rid);
    if (r.error) setError(r.error);
    else { setError(null); onChange(); }
  }

  async function clearAllRows() {
    if (sortedRevenues.length === 0) return;
    if (!window.confirm(`${p('Supprimer toutes les')} ${sortedRevenues.length} ${p('lignes ? Cette action est irréversible.')}`)) return;
    // Supprime d'abord les coûts marchandise liés (charges pct_revenue).
    const ids = new Set(sortedRevenues.map((rev) => rev.id));
    const linked = (variables || []).filter(
      (v) => v.driver_type === 'pct_revenue' && ids.has(v.revenue_ref_id)
    );
    for (const v of linked) {
      const rv = await deleteVariableExpense(tenantId, scenarioId, v.id);
      if (rv.error) { setError(rv.error); return; }
    }
    if (linked.length) onChangeVariables && onChangeVariables();
    const results = await Promise.all(
      sortedRevenues.map((rev) => deleteRevenue(tenantId, scenarioId, rev.id))
    );
    const firstError = results.find((r) => r.error);
    if (firstError) setError(firstError.error);
    else { setError(null); onChange(); }
  }

  return (
    <div className="billing-tab">
      {!hideTitle && <h2 className="tres-section-title">{p('Ventes')}</h2>}

      {error && (
        <div className="treasury__error">
          {error} <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {(() => {
        const totalHT = totalAnnualHt(sortedRevenues);
        const avgHT = totalHT / 12;
        const encMonthly = monthlyCashCollection(sortedRevenues, 0, startMonth);
        const totalEnc = encMonthly.reduce((s, v) => s + v, 0);
        const avgEnc = totalEnc / 12;
        return (
          <div className="billing-stats billing-stats--row">
            <div className="billing-stat">
              <span className="billing-stat__label">{p('Total facturé HT')}</span>
              <span className="billing-stat__value">{fmtK(totalHT)}</span>
            </div>
            <div className="billing-stat">
              <span className="billing-stat__label">{p('Moy. facturé / mois')}</span>
              <span className="billing-stat__value">{fmtK(avgHT)}</span>
            </div>
            <div className="billing-stat">
              <span className="billing-stat__label">{p('Total encaissé HT')}</span>
              <span className="billing-stat__value">{fmtK(totalEnc)}</span>
            </div>
            <div className="billing-stat">
              <span className="billing-stat__label">{p('Moy. encaissée / mois')}</span>
              <span className="billing-stat__value">{fmtK(avgEnc)}</span>
            </div>
          </div>
        );
      })()}

      {!locked && (
        <div className="billing-table__header">
          <AddRevenueMenu
            onSelectBlank={addBlankRow}
            onSelectPreset={addPresetRow}
            onLoadStandardPack={loadStandardPack}
          />
          {sortedRevenues.length > 0 && (
            <button
              type="button"
              className="billing-clear-btn"
              onClick={clearAllRows}
              title={t('phrases:p_53c46986f1')}
            >
              {p('Vider toutes les lignes')}
            </button>
          )}
        </div>
      )}

      <div className="billing-split">
        <RevenueList
          revenues={sortedRevenues}
          totalRevenue={totalRevenue}
          selectedId={selectedId}
          locked={locked}
          startMonth={startMonth}
          onSelect={setSelectedId}
          onPatch={(rid, payload) => patch(rid, payload)}
          onMove={swapOrder}
        />
        <RevenueDetailPanel
          revenue={selectedRevenue}
          colorIndex={isTotalSelected ? 0 : (selectedIndex >= 0 ? selectedIndex : 0)}
          startMonth={startMonth}
          monthLabels={monthLabels}
          locked={locked || isTotalSelected}
          showCogs={!isTotalSelected}
          isTotal={isTotalSelected}
          allRevenues={sortedRevenues}
          vatRate={Number(scenario?.vat_rate) || 0.081}
          variables={variables}
          tenantId={tenantId}
          scenarioId={scenarioId}
          onChangeVariables={onChangeVariables}
          onPatch={(payload) => selectedRevenue && !isTotalSelected && patch(selectedRevenue.id, payload)}
          onDuplicate={() => duplicateRow(selectedRevenue)}
          onDelete={() => removeRow(selectedRevenue?.id)}
        />
      </div>
    </div>
  );
}
